import { useMemo } from 'react';
import { useUser } from '@/hooks';
import { useSelectedUser } from '@/contexts/SelectedUserContext';
import { useOrgId } from '@/hooks';

/**
 * Hook to determine the effective user context for marketing operations
 * Handles both external users (viewing their own data) and internal users
 * (viewing selected external user's data)
 */
export const useMarketingUserContext = () => {
  const user = useUser();
  const { selectedUser } = useSelectedUser();
  const { orgId, loading: orgIdLoading, error: orgIdError } = useOrgId();

  // Determine the target user ID for MXM operations
  const targetUserId = useMemo(() => {
    return user?.role === 'external'
      ? user?.id
      : (selectedUser?.id || user?.id);
  }, [user?.role, user?.id, selectedUser?.id]);

  // Determine if we should use userId or orgId
  const isInternalUserViewingExternal = useMemo(() => {
    return user?.role === 'internal' && !!selectedUser?.id;
  }, [user?.role, selectedUser?.id]);

  const effectiveOrgId = useMemo(() => {
    return isInternalUserViewingExternal ? undefined : orgId;
  }, [isInternalUserViewingExternal, orgId]);

  const effectiveUserId = useMemo(() => {
    return isInternalUserViewingExternal ? targetUserId : undefined;
  }, [isInternalUserViewingExternal, targetUserId]);

  const hasValidContext = !!(effectiveOrgId || effectiveUserId);

  return {
    user,
    selectedUser,
    orgId,
    orgIdLoading,
    orgIdError,
    targetUserId,
    isInternalUserViewingExternal,
    effectiveOrgId,
    effectiveUserId,
    hasValidContext,
  };
};

